import { h } from 'preact';
import { useEffect } from 'preact/hooks';
import PropTypes from 'prop-types';

import { IconFlagGreen } from '../../components/1-foundation/icons/icons';
import Modal from '../../components/3-molecules/modal/modal';
import OverlayWrapper from '../../components/3-molecules/overlay-wrapper/overlay-wrapper';
import WPNDialog from '../../components/4-organisms/wpn-dialog/wpn-dialog';
import { commonActions } from '../../../global-store/subjects/common';
import { WPN_POPUP_STATUS } from '../../../global-store/subjects/auto-push-notifications/get-actions';
import { getLabel } from '../../../labels/labelling-service';
import { isIos } from '../../../utils/device-utils';
import { isEmptyObject } from '../../../utils/object-utils';
import {
  checkExpirationOnLocalStorage,
  WPN_STATUS,
  WPN_DAILY_SESSION,
  setStatusOnLocalStorage,
  WPN_SUBSCRIPTION,
} from '../../../utils/local-storage';

import { getHandlers } from './getters/get-handlers';
import { initInDialog } from './helpers/init-in-dialog';

const AutoPushNotifications = ({
  htmlProps,
  state,
  actions,
  elementIdInPage,
}) => {
  const handlers = getHandlers({ state, actions });
  const onUnsubscribe = state.sdk?.channel?.optedIn ? handlers.handleWPNunsubscribe : undefined;

  useEffect(() => {
    checkExpirationOnLocalStorage(WPN_STATUS, WPN_DAILY_SESSION);

    const commonInit = (async () => {
      try {
        actions.setIsLoading(true);
        if (isEmptyObject(window.UA) || isIos()) {
          throw new Error('[WPN] UA is empty. Cannot initialize Push Notification.');
        }
        const sdk = await window.UA;
        actions.setSdk(sdk);
        actions.setIsLoading(false);
      } catch (error) {
        actions.setSdk(null);
        commonActions.setPushNotificationInDialogShow(false);
        console.error(error);
        actions.setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (state.sdk) {
      if (state.sdk.channel?.optedIn) {
        setStatusOnLocalStorage({ key: WPN_SUBSCRIPTION.OBJECT_KEY, name: WPN_SUBSCRIPTION.SUBSCRIBED.NAME });
      }
      initInDialog({
        actions,
        htmlProps,
        selectBoxes: htmlProps.selectBoxes,
        sdk: state.sdk,
        handlers,
        onShowConfirmModal: () => {
          handlers.handleShowConfirmModal();
          setStatusOnLocalStorage({
            key: WPN_STATUS.OBJECT_KEY,
            name: WPN_STATUS.KO_SOFT.NAME,
            expirationDays: WPN_STATUS.KO_SOFT.EXPIRATION_DAYS,
          });
        },
        elementIdInPage,
      });
    }
  }, [state.sdk]);

  if (!state.sdk) {
    return null;
  }

  return (
      <OverlayWrapper showOverlay={state.popupStatus !== WPN_POPUP_STATUS.NOTHING_IS_SHOWN}>
        {state.popupStatus === WPN_POPUP_STATUS.SHOW_CONFIRM_MODAL && (
          <Modal
            onClose={() => {
              handlers.handleConfirmModalClose(htmlProps.selectBoxes);
              setStatusOnLocalStorage({
                key: WPN_STATUS.OBJECT_KEY,
                name: WPN_STATUS.KO_SOFT.NAME,
                expirationDays: WPN_STATUS.KO_SOFT.EXPIRATION_DAYS,
              });
            }}
            modifiers={['confirm']}
            title={getLabel('wpn.confirm.dialog.title')}
            description={htmlProps.confirmDialogDescriptionText}
            footerCtaList={[
              {
                modifiers: ['no-outline', 'capitalized'],
                action: () => {
                  handlers.handleConfirmModalCancel(htmlProps.selectBoxes);
                  setStatusOnLocalStorage({
                    key: WPN_STATUS.OBJECT_KEY,
                    name: WPN_STATUS.KO.NAME,
                    expirationDays: WPN_STATUS.KO.EXPIRATION_DAYS,
                  });
                },
                label: getLabel('wpn.confirm.dialog.cancel'),
              },
              {
                modifiers: ['no-outline'],
                action: () => {
                  handlers.handleWPNAccept(htmlProps.selectBoxes);
                  setStatusOnLocalStorage({
                    key: WPN_STATUS.OBJECT_KEY,
                    name: WPN_STATUS.OK.NAME,
                    expirationDays: WPN_STATUS.OK.EXPIRATION_DAYS,
                  });
                },
                label: getLabel('wpn.confirm.dialog.accept'),
                hasArrow: true,
              },
            ]}
          />
        )}
        {state.popupStatus === WPN_POPUP_STATUS.SHOW_CONFIRM_MODAL_SUCCESS && (
          <Modal
            onClose={handlers.handleGenericDialogClose}
            modifiers={['confirm', 'confirm-no-description']}
            title={getLabel('wpn.confirm.dialog.success.title')}
            iconComponent={<IconFlagGreen modifiers={['icon-top', 'flag-green']} />}
          />
        )}
        {state.popupStatus === WPN_POPUP_STATUS.SHOW_PREFERENCES_POPUP && (
          <WPNDialog
            title={getLabel('wpn.preferences.dialog.title')}
            selectBoxes={htmlProps.selectBoxes}
            onClose={() => handlers.handlePreferencesDialogClose(htmlProps.selectBoxes)}
            onConfirm={() => handlers.handlePreferencesDialogConfirm(htmlProps.selectBoxes, state.selectedOptions)}
            onUnsubscribe={onUnsubscribe}
            onOptionClick={handlers.handleOptionClick}
            selectedOptions={state.selectedOptions}
            onSwitchClick={handlers.handleSwitchClick}
          />
        )}
        {state.popupStatus === WPN_POPUP_STATUS.SHOW_PREFERENCES_UNSUBSCRIBE_CONFIRM && (
          <Modal
            modifiers={['confirm', 'confirm-no-description']}
            title={getLabel('wpn.preferences.unsub.dialog.title')}
            onClose={() => handlers.handlePreferencesDialogClose(htmlProps.selectBoxes)}
            footerCtaList={[
              {
                modifiers: ['no-outline'],
                action: handlers.handleWPNunsubscribeConfirm,
                label: getLabel('wpn.preferences.unsub.dialog.confirm'),
              },
              {
                modifiers: ['no-outline'],
                label: getLabel('wpn.preferences.unsub.dialog.cancel'),
                action: () => handlers.handlePreferencesDialogClose(htmlProps.selectBoxes),
              },
            ]}
          />
        )}
        {state.popupStatus === WPN_POPUP_STATUS.SHOW_PREFERENCES_UNSUB_SUCCESS && (
          <Modal
            onClose={handlers.handleGenericDialogClose}
            modifiers={['confirm', 'confirm-no-description']}
            title={getLabel('wpn.preferences.unsub.success.dialog.title')}
            iconComponent={<IconFlagGreen modifiers={['icon-top', 'flag-green']} />}
          />
        )}
      </OverlayWrapper>
    );
};

AutoPushNotifications.propTypes = {
  htmlProps: PropTypes.shape({
    confirmDialogDescriptionText: PropTypes.string,
    selectBoxes: PropTypes.array.isRequired,
  }),
  state: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  blockInPageType: PropTypes.string,
};

export default AutoPushNotifications;
